import { ConfigConstants } from '../../config/config-constants';
import { ArrayModel, JsonArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import {
    JsonSourceReplacementModel,
    SourceReplacementModel,
    SourceReplacementModelFromJson,
} from './source-replacement.model';
import { NotificationIdentifierModel } from '../low-level/notification-identifier.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { merge } from 'lodash';

export class ConfirmationSourceReplacementsModel extends CompositeModel {
    public sourceReplacements: ArrayModel<SourceReplacementModel>;

    public constructor(
        public notificationId: NotificationIdentifierModel,
        sourceReplacements: SourceReplacementModel[] = [],
    ) {
        super('ConfirmationSourceReplacementsModel');
        this.notificationId = notificationId;
        this.sourceReplacements = new ArrayModel<SourceReplacementModel>('SourceReplacements', sourceReplacements);
    }

    public getOutModel(): ODataConfirmationSourceReplacementsOutModel {
        const outModel = <ODataConfirmationSourceReplacementsOutModel>super.getOutModel();
        if (this.sourceReplacements.value.length > 0) {
            this.assignSourceNames();
        }
        const sourceReplacementsOutModels: ODataSourceReplacementsOutModel[] = this.sourceReplacements.value.map(
            (sourceReplacement: SourceReplacementModel) => {
                const sourceName = sourceReplacement.sourceName.value || '';
                const changeIndicatorCode = sourceReplacement.changeIndicatorCode || '';
                const sourceClassification = sourceReplacement.sourceClassification.value || '';
                const materialNumber = sourceReplacement.materialNumber.value || '';

                const calibrationValue = sourceReplacement.calibrationDate?.value;
                const calibrationDate =
                    calibrationValue && typeof calibrationValue.isValid === 'function' && calibrationValue.isValid()
                        ? calibrationValue.format(ConfigConstants.formatDateWithoutSplitter)
                        : '';

                const textLine = [
                    sourceName,
                    changeIndicatorCode,
                    sourceClassification,
                    materialNumber,
                    calibrationDate,
                ]
                    .filter((item) => item && item.trim() !== '')
                    .join(ConfigConstants.SOURCE_REPLACEMENT_TEXT_SPLITTER);

                return {
                    TaskNo: this.notificationId.maNum,
                    Notification: this.notificationId.qmNum,
                    TextID: 'SORC',
                    TextLine: textLine,
                } as ODataSourceReplacementsOutModel;
            },
        );

        merge(outModel, { CrmLongTextTRIDSet: sourceReplacementsOutModels });
        return outModel;
    }

    private assignSourceNames(): void {
        // Extract existing SourceName numbers
        const sourceNumbers = this.sourceReplacements.value
            .map((sourceReplacement: SourceReplacementModel) => {
                const match = sourceReplacement.sourceName?.value.match(/\d+$/);
                return match ? parseInt(match[0], 10) : null;
            })
            .filter((num): num is number => num !== null);

        // Find the highest existing SourceName number
        const maxSourceNumber = sourceNumbers.length > 0 ? Math.max(...sourceNumbers) : 0;

        let nextSourceNumber = maxSourceNumber + 1;

        // Assign new SourceName to newly added replacements
        this.sourceReplacements.value.forEach((sourceReplacement: SourceReplacementModel) => {
            if (!sourceReplacement.sourceName?.value || sourceReplacement.sourceName.value.trim() === '') {
                sourceReplacement.sourceName.value = `Source Name${nextSourceNumber++}`;
            }
        });
    }
}

export class ConfirmationSourceReplacementsModelFromJson extends ConfirmationSourceReplacementsModel {
    public constructor(data: JsonConfirmationSourceReplacementsModel, notificationId: NotificationIdentifierModel) {
        const sourceReplacements = data.sourceReplacements.value.map(
            (jsonSourceReplacement: JsonSourceReplacementModel) =>
                new SourceReplacementModelFromJson(jsonSourceReplacement),
        );
        super(notificationId, sourceReplacements);
    }
}

export interface JsonConfirmationSourceReplacementsModel {
    sourceReplacements: JsonArrayModel;
}

export interface ODataConfirmationSourceReplacementsOutModel extends ODataObject {
    CrmLongTextTRIDSet: ODataSourceReplacementsOutModel[];
}

export interface ODataSourceReplacementsOutModel extends ODataObject {
    Notification: string;
    TaskNo: string;
    TextID: string;
    TextLine: string;
}
